import React, { useCallback, useRef, useState } from "react";
import { Select, Spin } from "antd";
import type { SelectProps } from "antd";
import axios from "axios";
import { useApiUrl } from "@pankod/refine-core";

type Props = Omit<
  SelectProps<string[]>,
  "mode" | "options" | "onSearch" | "filterOption" | "loading" | "notFoundContent"
> & {
  // When true, restrict selection to values returned by the /quiz-keyword
  // endpoint (Select `mode="multiple"`). When false/omitted, allow free-form
  // creation of new tags (`mode="tags"`) — the default behavior used by the
  // quiz keywords field.
  strict?: boolean;
};

const KeywordTagsSelect: React.FC<Props> = ({ strict, ...props }) => {
  const apiUrl = useApiUrl();
  const APIToken = localStorage.getItem("api-token");
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback(
    (value: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (!value || value.length < 3) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      timeoutRef.current = setTimeout(() => {
        axios
          .get(`${apiUrl}/quiz-keyword`, {
            // No `%` prefix here — the /quiz-keyword backend doesn't accept
            // SQL LIKE wildcards in `q` (unlike /quiz which does).
            params: { q: value },
            headers: { Authorization: `Bearer ${APIToken}` },
          })
          .then((res) => {
            const items: string[] = Array.isArray(res.data) ? res.data : [];
            setOptions(items.map((k) => ({ label: k, value: k })));
          })
          .catch(() => setOptions([]))
          .finally(() => setLoading(false));
      }, 300);
    },
    [apiUrl, APIToken]
  );

  return (
    <Select
      {...props}
      mode={strict ? "multiple" : "tags"}
      onSearch={handleSearch}
      options={options}
      filterOption={false}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : null}
      tokenSeparators={strict ? undefined : [","]}
    />
  );
};

export default KeywordTagsSelect;
